import { HStack, Link, SimpleGrid, Text } from '@cardboard-ui/react';
import { t, Trans } from '@lingui/macro';
import FavouriteIcon from 'apps/TenantApp/screens/Favourites/components/FavouriteIcon';
import { SubtitleIconButton } from 'apps/TenantApp/screens/Start';
import { CloseIcon, SettingsWheelIcon } from 'components/icons';
import { SuspendWithRelativelyCenteredSpinner } from 'components/suspense/SuspendWithRelativelyCenteredSpinner';
import { useLocalStorage } from 'hooks/useBrowserStorage';
import React, { PropsWithChildren } from 'react';
import { ErrorBoundary } from 'utils/errors';
import { graphql, useFragment } from 'utils/graphClient';
import { Layout, useActiveLayout } from 'utils/LayoutProvider';
import { notEmpty } from 'utils/NotEmptyFilter';
import {
  FAVOURITES_PATH_NAME,
  MY_SETTINGS_QUERY_PARAM_NAME,
} from 'utils/routes';
import { CollapsingMenuSection } from './CollapsingMenuSection';
import { FavouriteLink } from './FavouriteLink';
import { MenuItemStack } from './MenuItemStack';
import { MenuSubtitle } from './MenuSubtitle';
import { FavouritesMenuSection$key } from './__generated__/FavouritesMenuSection.graphql';

const favouritesQuery = graphql`
  fragment FavouritesMenuSection on SessionInfo {
    favourites(first: 500) @connection(key: "FavouritesContent_favourites") {
      edges {
        node {
          id
          node {
            ...FavouriteLink_node
          }
        }
      }
    }
  }
`;

interface FavouritesMenuSectionProps {
  isMobile?: boolean;
  favouritesFragment: FavouritesMenuSection$key;
}

const HIDE_FAVORITES_MENU_SECTION_STORAGE_KEY = 'hide-favourites-menu-section';

const UnsuspendedFavouritesMenuSection = (
  props: FavouritesMenuSectionProps,
) => {
  const sessionInfo = useFragment(favouritesQuery, props.favouritesFragment);
  const activeLayout = useActiveLayout();
  const isMobile = props.isMobile || activeLayout === Layout.MOBILE;
  const [_hideFavoritesMenuSection, setHideFavoritesMenuSection] =
    useLocalStorage(HIDE_FAVORITES_MENU_SECTION_STORAGE_KEY, false, 'device');

  const favourites =
    sessionInfo.favourites?.edges
      .map((f) => f.node)
      .filter(notEmpty)
      .filter(({ node }) => notEmpty(node))
      .slice(0, 5) || [];

  const hideFavoritesMenuSection =
    _hideFavoritesMenuSection && !favourites.length;

  if (hideFavoritesMenuSection) {
    return null;
  }

  const ListWrap = isMobile ? MobileGrid : React.Fragment;

  return (
    <CollapsingMenuSection label={t`Favourites`} icon={<FavouriteIcon />}>
      <MenuItemStack>
        <MenuSubtitle
          rightButton={
            <HStack gap={0}>
              <SubtitleIconButton
                as={Link}
                to={`?${MY_SETTINGS_QUERY_PARAM_NAME}=${FAVOURITES_PATH_NAME}`}
                aria-label={t`Favourites settings`}
                icon={<SettingsWheelIcon />}
              />
              <SubtitleIconButton
                active={!favourites.length && !isMobile}
                aria-label={t`Hide favourites settings`}
                icon={<CloseIcon />}
                onClick={() => {
                  setHideFavoritesMenuSection(true);
                }}
              />
            </HStack>
          }
        >
          {t`Favourites`}
        </MenuSubtitle>

        <ListWrap>
          {favourites.map(
            (f) =>
              f.node && <FavouriteLink {...props} key={f.id} node={f.node} />,
          )}
        </ListWrap>

        {!favourites.length && (
          <Trans>
            <Text fontSize="sm" fontStyle="italic">
              Add any item to this menu by <FavouriteIcon isFavourite />{' '}
              favouriting it.
            </Text>
          </Trans>
        )}
      </MenuItemStack>
    </CollapsingMenuSection>
  );
};

export const FavouritesMenuSection = (props: FavouritesMenuSectionProps) => (
  <SuspendWithRelativelyCenteredSpinner>
    <ErrorBoundary
      fallback={<Text fontSize="sm">{t`Favourites could not be loaded`}</Text>}
    >
      <UnsuspendedFavouritesMenuSection {...props} />
    </ErrorBoundary>
  </SuspendWithRelativelyCenteredSpinner>
);

const MobileGrid = (props: PropsWithChildren) => (
  <SimpleGrid
    minChildWidth="160px"
    columnGap={4}
    rowGap={4}
    autoRows="1fr"
    w="100%"
    {...props}
  />
);
